import { useState } from 'react';
import { Button, useToast } from '@vd/ui';
import { post } from '../../lib/apiClient';
import {
  addonName, getMoreChoices, type AddonKey, type AddonsState, type GetMoreChoice, type Meter,
} from './addonOffers';
import { formatDay as formatPersonDay } from '../../lib/time';

/**
 * The billing page's add-on controls (TOADO-660): "Get more" on a meter for
 * the owner, "Ask the owner" for everyone else, and the add-ons a workspace
 * holds. Every purchase goes through Stripe Checkout; the server decides what
 * is on offer and grants only what Stripe says was paid.
 */

async function buyAddon(companyId: string, addon: AddonKey): Promise<string> {
  return (await post<{ url: string }>('/v1/billing/addons/checkout', { companyId, addon, units: 1 })).url;
}

interface CommonProps {
  companyId: string;
  /** False on an environment without Stripe: buttons explain instead of redirecting. */
  billingConfigured: boolean;
}

export function GetMore({ meter, addons, plan, isOwner, contactUrl, onPlan, companyId, billingConfigured, businessOnSale = false }: CommonProps & {
  meter: Meter;
  addons: AddonsState | undefined;
  plan: string;
  /** Business is on sale here, so Team can be offered it (TOADO-662). */
  businessOnSale?: boolean;
  isOwner: boolean;
  contactUrl: string;
  /** Move to a plan the way the plan cards do (Checkout from Free, the portal from a paid plan). */
  onPlan: (plan: 'personal' | 'team' | 'business') => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  if (!isOwner) {
    const ask = async () => {
      setBusy('ask');
      try {
        const answer = await post<{ sent: boolean }>('/v1/billing/ask-owner', { companyId, meter });
        toast.push({
          variant: 'info',
          title: answer.sent ? 'Asked the owner' : 'Already asked today',
          description: answer.sent ? 'The workspace owners have been emailed.' : 'The owners were emailed earlier today.',
        });
      } catch (error) {
        toast.push({ variant: 'danger', title: 'Could not ask the owner', description: (error as Error).message });
      } finally {
        setBusy(null);
      }
    };
    return (
      <Button variant="secondary" size="sm" onClick={ask} disabled={busy === 'ask'} data-testid={`ask-owner-${meter}`}>
        {busy === 'ask' ? 'Asking…' : 'Ask the owner'}
      </Button>
    );
  }

  const choose = async (choice: GetMoreChoice) => {
    if (choice.action.kind === 'contact') {
      window.open(contactUrl, '_blank', 'noopener');
      return;
    }
    if (choice.action.kind === 'plan') {
      onPlan(choice.action.plan);
      return;
    }
    if (!billingConfigured) {
      toast.push({ variant: 'info', title: 'Billing not configured (dev mode)', description: 'Set the STRIPE_* variables on the api to buy add-ons.' });
      return;
    }
    setBusy(choice.key);
    try {
      window.location.assign(await buyAddon(companyId, choice.action.addon));
    } catch (error) {
      toast.push({ variant: 'danger', title: 'Could not start checkout', description: (error as Error).message });
      setBusy(null);
    }
  };

  const choices = getMoreChoices(meter, addons, plan, businessOnSale);
  return (
    <div style={{ position: 'relative' }}>
      <Button variant="secondary" size="sm" onClick={() => setOpen((value) => !value)} aria-expanded={open} data-testid={`get-more-${meter}`}>
        Get more
      </Button>
      {open && (
        <div
          role="menu"
          data-testid={`get-more-${meter}-menu`}
          style={{
            position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 5, width: 300, maxWidth: '80vw',
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 6,
            boxShadow: 'var(--shadow-md, 0 8px 24px rgba(0,0,0,0.12))', display: 'grid', gap: 2,
          }}
        >
          {choices.map((choice) => (
            <button
              key={choice.key}
              type="button"
              role="menuitem"
              disabled={busy !== null}
              onClick={() => { void choose(choice); }}
              style={{
                textAlign: 'left', background: 'none', border: 0, borderRadius: 8, padding: '8px 10px',
                cursor: 'pointer', color: 'var(--text)', display: 'grid', gap: 2,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600 }}>{busy === choice.key ? 'Redirecting…' : choice.label}</span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{choice.detail}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** An add-on's end, as the day it falls on in the person's zone (TOADO-904). */
function formatDay(iso: string): string {
  return formatPersonDay(iso);
}

/**
 * The add-ons a workspace holds: what renews on the 1st (with Change and
 * Cancel for the owner), the packs it has left, and video history.
 */
export function ActiveAddons({ addons, isOwner, companyId, billingConfigured, videoHistory, onChanged }: CommonProps & {
  addons: AddonsState;
  isOwner: boolean;
  videoHistory: { active: boolean; through: string | null } | undefined;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const renewing = Object.entries(addons.renewing).filter(([, units]) => (units ?? 0) > 0) as Array<['narration_boost' | 'recording_boost' | 'video_history', number]>;
  const videoOffer = addons.offers.find((offer) => offer.addon === 'video_history');
  const offerVideo = isOwner && videoOffer && !addons.renewing.video_history;
  if (renewing.length === 0 && addons.packs.length === 0 && !offerVideo && !videoHistory?.active) return null;

  const change = async (addon: 'narration_boost' | 'recording_boost' | 'video_history', units: number) => {
    setBusy(addon);
    try {
      await post('/v1/billing/addons/change', { companyId, addon, units });
      toast.push({ variant: 'success', title: units === 0 ? 'Stops renewing' : 'Changed', description: 'From the 1st. This month stays as it is.' });
      onChanged();
    } catch (error) {
      toast.push({ variant: 'danger', title: 'Could not change it', description: (error as Error).message });
    } finally {
      setBusy(null);
    }
  };

  const buyVideo = async () => {
    if (!billingConfigured) {
      toast.push({ variant: 'info', title: 'Billing not configured (dev mode)', description: 'Set the STRIPE_* variables on the api to buy add-ons.' });
      return;
    }
    setBusy('video');
    try {
      window.location.assign(await buyAddon(companyId, 'video_history'));
    } catch (error) {
      toast.push({ variant: 'danger', title: 'Could not start checkout', description: (error as Error).message });
      setBusy(null);
    }
  };

  return (
    <section data-testid="active-addons" style={{ display: 'grid', gap: 10 }}>
      <h3 style={{ fontSize: 14, margin: 0 }}>Add-ons</h3>
      {renewing.map(([addon, units]) => (
        <div key={addon} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
          <span>
            {addonName(addon)}{addon === 'video_history' ? '' : ` × ${units}`}
            <span style={{ color: 'var(--text-tertiary)' }}> · renews on the 1st</span>
          </span>
          {isOwner && (
            <span style={{ display: 'flex', gap: 6 }}>
              {addon !== 'video_history' && units > 1 && (
                <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => { void change(addon, units - 1); }}>
                  One fewer
                </Button>
              )}
              <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => { void change(addon, 0); }} data-testid={`cancel-${addon}`}>
                {busy === addon ? 'Saving…' : 'Cancel'}
              </Button>
            </span>
          )}
        </div>
      ))}
      {addons.packs.map((pack) => (
        <div key={`${pack.addon}-${pack.expiresAt}`} style={{ fontSize: 13 }}>
          {addonName(pack.addon)}: {pack.minutesLeft} minutes left
          <span style={{ color: 'var(--text-tertiary)' }}> · until {formatDay(pack.expiresAt)}</span>
        </div>
      ))}
      {videoHistory?.active && !addons.renewing.video_history && videoHistory.through && (
        <div style={{ fontSize: 13 }}>
          Video history: on until {formatDay(videoHistory.through)}
        </div>
      )}
      {offerVideo && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
          <span>
            Keep walkthrough video for a year instead of your plan&apos;s window
            <span style={{ color: 'var(--text-tertiary)' }}> · ${(videoOffer!.centsPerUnit / 100).toFixed(0)} a seat a month</span>
          </span>
          <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => { void buyVideo(); }} data-testid="buy-video-history">
            {busy === 'video' ? 'Redirecting…' : 'Add video history'}
          </Button>
        </div>
      )}
    </section>
  );
}
